import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Format bytes as human-readable text.
 * @param bytes Number of bytes.
 * @param si True to use metric (SI) units, false for binary (IEC).
 * @param dp Number of decimal places to display.
 */
export function formatBytes(bytes: number, si = false, dp = 1) {
  const thresh = si ? 1000 : 1024

  if (Math.abs(bytes) < thresh) {
    return bytes + ' B'
  }

  const units = si
    ? ['kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
    : ['KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB']
  let u = -1
  const r = 10 ** dp

  do {
    bytes /= thresh
    ++u
  } while (Math.round(Math.abs(bytes) * r) / r >= thresh && u < units.length - 1)

  return bytes.toFixed(dp) + ' ' + units[u]
}

/**
 * Format a date as a relative time string.
 */
export function formatRelativeTime(date: string | Date): string {
  const now = new Date()
  const targetDate = typeof date === 'string' ? new Date(date) : date
  const diffInSeconds = Math.floor((now.getTime() - targetDate.getTime()) / 1000)

  if (diffInSeconds < 60) {
    return 'just now'
  }

  const diffInMinutes = Math.floor(diffInSeconds / 60)
  if (diffInMinutes < 60) {
    return `${diffInMinutes} minute${diffInMinutes === 1 ? '' : 's'} ago`
  }

  const diffInHours = Math.floor(diffInMinutes / 60)
  if (diffInHours < 24) {
    return `${diffInHours} hour${diffInHours === 1 ? '' : 's'} ago`
  }

  const diffInDays = Math.floor(diffInHours / 24)
  if (diffInDays < 30) {
    return `${diffInDays} day${diffInDays === 1 ? '' : 's'} ago`
  }

  const diffInMonths = Math.floor(diffInDays / 30)
  if (diffInMonths < 12) {
    return `${diffInMonths} month${diffInMonths === 1 ? '' : 's'} ago`
  }

  const diffInYears = Math.floor(diffInMonths / 12)
  return `${diffInYears} year${diffInYears === 1 ? '' : 's'} ago`
}

/**
 * Format a date in a human-readable format.
 */
export function formatDate(date: string | Date, options?: Intl.DateTimeFormatOptions): string {
  const targetDate = typeof date === 'string' ? new Date(date) : date

  return targetDate.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    ...options,
  })
}

/**
 * Validate Docker repository name according to Docker standards.
 */
export function validateRepositoryName(name: string): { isValid: boolean; errors: string[] } {
  const errors: string[] = []

  if (!name) {
    errors.push('Repository name is required')
    return { isValid: false, errors }
  }

  if (name.length > 255) {
    errors.push('Repository name must be 255 characters or less')
  }

  // Docker naming convention: [a-z0-9]+(?:[._-][a-z0-9]+)*
  const dockerNamePattern = /^[a-z0-9]+(?:[._-][a-z0-9]+)*$/
  if (!dockerNamePattern.test(name)) {
    errors.push(
      'Repository name must be lowercase and contain only letters, numbers, periods, underscores, and hyphens'
    )
  }

  // Cannot start or end with separators
  if (
    name.startsWith('.') ||
    name.startsWith('_') ||
    name.startsWith('-') ||
    name.endsWith('.') ||
    name.endsWith('_') ||
    name.endsWith('-')
  ) {
    errors.push('Repository name cannot start or end with periods, underscores, or hyphens')
  }

  // Cannot have consecutive separators
  if (/[._-]{2,}/.test(name)) {
    errors.push('Repository name cannot contain consecutive periods, underscores, or hyphens')
  }

  return { isValid: errors.length === 0, errors }
}

/**
 * Validate Docker image tag according to Docker standards.
 */
export function validateImageTag(tag: string): { isValid: boolean; errors: string[] } {
  const errors: string[] = []

  if (!tag) {
    errors.push('Image tag is required')
    return { isValid: false, errors }
  }

  if (tag.length > 128) {
    errors.push('Image tag must be 128 characters or less')
  }

  // Docker tag pattern: [a-zA-Z0-9_.-]+
  const dockerTagPattern = /^[a-zA-Z0-9_.-]+$/
  if (!dockerTagPattern.test(tag)) {
    errors.push('Image tag can only contain letters, numbers, underscores, periods, and hyphens')
  }

  // Cannot start with period or hyphen
  if (tag.startsWith('.') || tag.startsWith('-')) {
    errors.push('Image tag cannot start with a period or hyphen')
  }

  return { isValid: errors.length === 0, errors }
}

/**
 * Validate email address format.
 */
export function validateEmail(email: string): { isValid: boolean; errors: string[] } {
  const errors: string[] = []

  if (!email) {
    errors.push('Email address is required')
    return { isValid: false, errors }
  }

  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  if (!emailPattern.test(email)) {
    errors.push('Please enter a valid email address')
  }

  return { isValid: errors.length === 0, errors }
}

/**
 * Validate password strength.
 */
export function validatePassword(password: string): {
  isValid: boolean
  errors: string[]
  strength: 'weak' | 'medium' | 'strong'
} {
  const errors: string[] = []
  let score = 0

  if (!password) {
    errors.push('Password is required')
    return { isValid: false, errors, strength: 'weak' }
  }

  if (password.length < 8) {
    errors.push('Password must be at least 8 characters long')
  } else {
    score += 1
  }

  if (!/[a-z]/.test(password)) {
    errors.push('Password must contain at least one lowercase letter')
  } else {
    score += 1
  }

  if (!/[A-Z]/.test(password)) {
    errors.push('Password must contain at least one uppercase letter')
  } else {
    score += 1
  }

  if (!/\d/.test(password)) {
    errors.push('Password must contain at least one number')
  } else {
    score += 1
  }

  if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
    errors.push('Password must contain at least one special character')
  } else {
    score += 1
  }

  const strength = score <= 2 ? 'weak' : score <= 4 ? 'medium' : 'strong'

  return { isValid: errors.length === 0, errors, strength }
}

/**
 * Truncate text to specified length with ellipsis.
 */
export function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) {
    return text
  }
  return text.slice(0, maxLength - 3) + '...'
}

/**
 * Truncate hash for display while maintaining readability.
 */
export function truncateHash(hash: string, prefixLength = 12, suffixLength = 4): string {
  if (hash.length <= prefixLength + suffixLength + 3) {
    return hash
  }
  return `${hash.slice(0, prefixLength)}...${hash.slice(-suffixLength)}`
}

/**
 * Generate a random ID.
 */
export function generateId(): string {
  return Math.random().toString(36).substr(2, 9)
}

/**
 * Copy text to clipboard.
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch (error) {
    // Fallback for older browsers
    const textArea = document.createElement('textarea')
    textArea.value = text
    textArea.style.position = 'fixed'
    textArea.style.left = '-999999px'
    textArea.style.top = '-999999px'
    document.body.appendChild(textArea)
    textArea.focus()
    textArea.select()

    try {
      document.execCommand('copy')
      document.body.removeChild(textArea)
      return true
    } catch (err) {
      document.body.removeChild(textArea)
      return false
    }
  }
}

/**
 * Debounce function to limit the rate of function calls.
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null

  return (...args: Parameters<T>) => {
    if (timeout) {
      clearTimeout(timeout)
    }
    timeout = setTimeout(() => func(...args), wait)
  }
}

/**
 * Check if a file is a valid Docker image format.
 */
export function isValidDockerImageFile(file: File): boolean {
  const validTypes = [
    'application/x-tar',
    'application/gzip',
    'application/x-gzip',
    'application/zip',
    'application/octet-stream',
  ]

  const validExtensions = ['.tar', '.tar.gz', '.tgz', '.zip']

  return (
    validTypes.includes(file.type) ||
    validExtensions.some((ext) => file.name.toLowerCase().endsWith(ext))
  )
}

/**
 * Calculate upload speed in bytes per second.
 */
export function calculateUploadSpeed(
  bytesUploaded: number,
  startTime: number,
  currentTime: number = Date.now()
): number {
  const timeElapsed = (currentTime - startTime) / 1000 // Convert to seconds
  if (timeElapsed === 0) return 0
  return bytesUploaded / timeElapsed
}

/**
 * Estimate remaining upload time in seconds.
 */
export function estimateRemainingTime(
  totalBytes: number,
  bytesUploaded: number,
  uploadSpeed: number
): number {
  if (uploadSpeed === 0) return Infinity
  const remainingBytes = totalBytes - bytesUploaded
  return remainingBytes / uploadSpeed
}

/**
 * Format upload speed for display.
 */
export function formatUploadSpeed(bytesPerSecond: number): string {
  return `${formatBytes(bytesPerSecond)}/s`
}

/**
 * Format remaining time for display.
 */
export function formatRemainingTime(seconds: number): string {
  if (seconds === Infinity || isNaN(seconds)) {
    return 'calculating...'
  }

  if (seconds < 60) {
    return `${Math.round(seconds)}s`
  }

  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = Math.round(seconds % 60)

  if (minutes < 60) {
    return `${minutes}m ${remainingSeconds}s`
  }

  const hours = Math.floor(minutes / 60)
  const remainingMinutes = minutes % 60

  return `${hours}h ${remainingMinutes}m`
}

/**
 * Get status color class based on image status.
 */
export function getStatusColor(status: string): string {
  switch (status.toLowerCase()) {
    case 'ready':
    case 'completed':
    case 'success':
      return 'text-status-ready'
    case 'building':
    case 'pending':
    case 'processing':
      return 'text-status-building'
    case 'failed':
    case 'error':
      return 'text-status-failed'
    case 'deployed':
      return 'text-status-deployed'
    case 'deprecated':
      return 'text-status-deprecated'
    default:
      return 'text-muted-foreground'
  }
}

/**
 * Get status background color class based on image status.
 */
export function getStatusBgColor(status: string): string {
  switch (status.toLowerCase()) {
    case 'ready':
    case 'completed':
    case 'success':
      return 'bg-green-100 text-green-800'
    case 'building':
    case 'pending':
    case 'processing':
      return 'bg-yellow-100 text-yellow-800'
    case 'failed':
    case 'error':
      return 'bg-red-100 text-red-800'
    case 'deployed':
      return 'bg-blue-100 text-blue-800'
    case 'deprecated':
      return 'bg-orange-100 text-orange-800'
    default:
      return 'bg-gray-100 text-gray-800'
  }
}

/**
 * Check if user has permission for a specific action.
 */
export function hasPermission(userRole: string, requiredRole: string | string[]): boolean {
  const roles = Array.isArray(requiredRole) ? requiredRole : [requiredRole]

  // Admin has all permissions
  if (userRole === 'Admin') {
    return true
  }

  return roles.includes(userRole)
}

/**
 * Sort array of objects by a specific field.
 */
export function sortBy<T>(array: T[], field: keyof T, direction: 'asc' | 'desc' = 'asc'): T[] {
  return [...array].sort((a, b) => {
    const aVal = a[field]
    const bVal = b[field]

    if (aVal === bVal) return 0

    const comparison = aVal < bVal ? -1 : 1
    return direction === 'asc' ? comparison : -comparison
  })
}

/**
 * Filter array based on search query.
 */
export function searchFilter<T>(items: T[], query: string, searchFields: (keyof T)[]): T[] {
  if (!query.trim()) {
    return items
  }

  const lowercaseQuery = query.toLowerCase()

  return items.filter((item) =>
    searchFields.some((field) => {
      const value = item[field]
      if (typeof value === 'string') {
        return value.toLowerCase().includes(lowercaseQuery)
      }
      return false
    })
  )
}

/**
 * Get user initials from a name string
 * Pattern: Single name -> First letter, Two names -> First letter of each, 3+ names -> First + Second word first letters
 * @param name - The full name string
 * @returns Initials (e.g., "V" for "vijay", "VK" for "Vijay Kumar", "SS" for "Siva Sakthi Velan", "JJ" for "john jobo raj Kumar")
 */
export const getInitials = (name: string): string => {
  if (!name || name.trim() === '') {
    return 'U' // Default for unknown/empty names
  }

  const words = name
    .trim()
    .split(/\s+/)
    .filter((word) => word.length > 0)

  if (words.length === 0) {
    return 'U'
  }

  if (words.length === 1) {
    // Single name: Use first letter only (e.g., "Karan" -> "K", "vijay" -> "V")
    return words[0].charAt(0).toUpperCase()
  }

  if (words.length === 2) {
    // Two names: Use first letter of each word (e.g., "Vijay Kumar" -> "VK")
    return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase()
  }

  // Three or more names: Use first letter of first and second words
  // (e.g., "Siva Sakthi Velan" -> "SS", "john jobo raj Kumar" -> "JJ")
  return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase()
}
